import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import {
  verifyEmail,
  resendVerification,
  resetEmailVerificationState,
} from '../../store/slices/authSlice';

const VerifyEmailPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { token } = useParams<{ token: string }>();

  const { loading, error, emailVerificationSuccess } = useSelector(
    (state: RootState) => state.auth,
  );

  // Local state for the "resend verification" form shown when verification fails.
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) {
      return;
    }
    setResendStatus('sending');
    setResendError(null);
    try {
      await dispatch(resendVerification(resendEmail)).unwrap();
      setResendStatus('sent');
    } catch (err) {
      setResendStatus('error');
      setResendError(typeof err === 'string' ? err : 'Failed to resend verification email.');
    }
  };

  // Resend-verification form, reused when a verification link fails and when the
  // page is opened without a token (e.g. from the "resend" link on the login page).
  const resendBlock =
    resendStatus === 'sent' ? (
      <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
        If an account with that email exists and is not yet verified, a new verification email has
        been sent. Please check your inbox.
      </div>
    ) : (
      <form onSubmit={handleResend}>
        <label htmlFor="resend-email" className="block text-sm font-medium text-gray-700">
          Enter your email to resend the verification message.
        </label>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <input
            id="resend-email"
            type="email"
            required
            value={resendEmail}
            onChange={e => setResendEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 appearance-none rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500"
          />
          <button
            type="submit"
            disabled={resendStatus === 'sending'}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {resendStatus === 'sending' ? 'Sending...' : 'Resend email'}
          </button>
        </div>
        {resendError && <p className="mt-2 text-sm text-danger-700">{resendError}</p>}
      </form>
    );

  // Verify email on component mount
  useEffect(() => {
    if (token) {
      dispatch(verifyEmail(token));
    }
  }, [dispatch, token]);

  // Clear state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetEmailVerificationState());
    };
  }, [dispatch]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Email Verification
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {loading ? (
            <div className="text-center">
              <svg
                className="animate-spin h-10 w-10 text-primary-600 mx-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <p className="mt-4 text-sm text-gray-600">Verifying your email...</p>
            </div>
          ) : emailVerificationSuccess ? (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-green-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Email verified successfully
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>Your email has been verified. You can now log in to your account.</p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/login"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Go to login
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="rounded-md bg-danger-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-danger-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-danger-800">Verification failed</h3>
                  <div className="mt-2 text-sm text-danger-700">
                    <p>{error}</p>
                    <p className="mt-2">The verification link may be invalid or expired.</p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/login"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-danger-700 bg-danger-100 hover:bg-danger-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500"
                    >
                      Back to login
                    </Link>
                  </div>

                  <div className="mt-6 border-t border-danger-200 pt-4">{resendBlock}</div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
